import { SVGProps } from 'react';

/* ============================================================
   UI action icons
   ------------------------------------------------------------
   Replaces Unicode-character icons (✕ ↺ ★ ⇪ ◎ ◐ ◑) that show
   as missing-glyph "?" in WKWebView when the WebKit font
   fallback chain can't resolve them — SF Pro doesn't carry
   every Geometric-Shapes or Supplemental-Arrows codepoint.
   These are plain inline SVGs with stroke="currentColor",
   so they pick up the button's text colour automatically.
   ============================================================ */

type IconProps = SVGProps<SVGSVGElement>;

const STROKE_WIDTH = 2;

function commonProps(props: IconProps) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: STROKE_WIDTH,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    width: 20,
    height: 20,
    'aria-hidden': true as const,
    ...props,
  };
}

/** ✕ — Close / Cancel */
export function CloseIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <path d="M6 6 L18 18" />
      <path d="M18 6 L6 18" />
    </svg>
  );
}

/** ↺ — Counter-clockwise circular arrow (Retake / Reset). */
export function RetakeIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      {/* arc covering ~280° anticlockwise */}
      <path d="M5 12 A 7 7 0 1 0 12 5" />
      {/* arrow head at the start (top-left) */}
      <path d="M5 7.5 L5 12 L9.5 12" />
    </svg>
  );
}

/** ★ — Filled star (Save). */
export function SaveIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <path
        d="M12 3 L14.5 9 L21 9.5 L16 13.5 L17.5 20 L12 16.5 L6.5 20 L8 13.5 L3 9.5 L9.5 9 Z"
        fill="currentColor"
        stroke="currentColor"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** ⇪ — Square with up-arrow exiting the top (iOS-style share/export). */
export function ExportIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      {/* up arrow */}
      <path d="M12 3 L12 14" />
      <path d="M7.5 7 L12 3 L16.5 7" />
      {/* tray under it */}
      <path d="M5 12 L5 20 L19 20 L19 12" />
    </svg>
  );
}

/** ◎ — Bullseye (Set Zero reference). */
export function ZeroIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** ◐ — Left half filled circle (Start capture). */
export function StartCaptureIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3 A 9 9 0 0 0 12 21 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** ◑ — Right half filled circle (End capture). */
export function EndCaptureIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3 A 9 9 0 0 1 12 21 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** 👁 — Eye open, used to reveal password inputs. */
export function EyeIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <path d="M2 12 C 5 6, 10 4, 12 4 C 14 4, 19 6, 22 12 C 19 18, 14 20, 12 20 C 10 20, 5 18, 2 12 Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** 👁‍🗨 — Eye crossed out, used to hide password inputs. */
export function EyeOffIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <path d="M2 12 C 5 6, 10 4, 12 4 C 14 4, 19 6, 22 12 C 19 18, 14 20, 12 20 C 10 20, 5 18, 2 12 Z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M3 3 L21 21" />
    </svg>
  );
}

/** › — Right chevron, used for navigable list rows (iOS Settings style). */
export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...commonProps(props)}>
      <path d="M9 6 L15 12 L9 18" />
    </svg>
  );
}
